import React, { useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FaEnvelope, FaLock } from 'react-icons/fa';
import api from '../../services/api';
import notify from '../../services/notify';
import '../Login/styles.css';
import './styles.css';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function RecuperarSenha() {
  const history = useHistory();
  const query = useQuery();
  const token = query.get('token') || '';
  const emailUrl = query.get('email') || '';
  const isResetMode = Boolean(token && emailUrl);

  const [email, setEmail] = useState(emailUrl);
  const [senha, setSenha] = useState('');
  const [senhaConfirmacao, setSenhaConfirmacao] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSolicitar(e) {
    e.preventDefault();
    const emailNormalizado = (email || '').trim();
    if (!emailNormalizado) {
      notify.warning('Informe seu e-mail.');
      return;
    }

    setLoading(true);
    try {
      const response = await api({
        method: 'post',
        action: '/recuperar-senha',
        data: { email: emailNormalizado }
      });
      notify.success(response?.data?.mensagem || 'Se o e-mail existir, enviaremos um link.');
    } catch (err) {
      notify.apiError(err, 'Não foi possível solicitar a recuperação.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRedefinir(e) {
    e.preventDefault();
    const emailNormalizado = (email || '').trim();
    const novaSenha = (senha || '').trim();
    const confirmarSenha = (senhaConfirmacao || '').trim();

    if (!emailNormalizado) {
      notify.warning('Link inválido. E-mail não informado.');
      return;
    }

    if (!token) {
      notify.warning('Link inválido. Token de recuperação ausente.');
      return;
    }

    if (!novaSenha) {
      notify.warning('Informe a nova senha.');
      return;
    }

    if (!confirmarSenha) {
      notify.warning('Confirme a nova senha.');
      return;
    }

    if (novaSenha !== confirmarSenha) {
      notify.error('As senhas não conferem.');
      return;
    }

    setLoading(true);
    try {
      const response = await api({
        method: 'post',
        action: '/redefinir-senha',
        data: { email: emailNormalizado, token, senha: novaSenha, senhaConfirmacao: confirmarSenha }
      });
      notify.success(response?.data?.mensagem || 'Senha redefinida com sucesso.');
      history.push('/login');
    } catch (err) {
      notify.apiError(err, 'Não foi possível redefinir a senha.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div id="auth" className="login-auth-shell">
      <div className="login-split-layout">
        <section
          className="login-hero-panel"
          aria-hidden="true"
          style={{ backgroundImage: 'url(/imagens/evento.png)' }}
        >
          <div className="login-hero-overlay" />
          <div className="login-hero-orb login-hero-orb--one" />
          <div className="login-hero-orb login-hero-orb--two" />
          <div className="login-hero-line login-hero-line--one" />
          <div className="login-hero-line login-hero-line--two" />
          <div className="login-hero-content">
            <p className="login-hero-kicker">Plataforma de Gerenciamento de Eventos</p>
            <h1 className="login-hero-title">Gestão de eventos da concepção à entrega.</h1>
            <div className="login-hero-tagline">
              <span>#A GENTE</span>
              <span>ENTREGA,</span>
              <span>E PONTO.</span>
            </div>
          </div>
        </section>

        <section className="login-form-panel">
          <div className="login-form-card password-recovery-card">
            <div className="login-form-brand password-recovery-brand">
              <img alt="Access" src="/logo192.png" className="login-form-logo" />
              <h1>{isResetMode ? 'Redefinir senha' : 'Recuperar senha'}</h1>
              <p>
                {isResetMode
                  ? 'Defina uma nova senha para acessar o sistema.'
                  : 'Informe seu e-mail para receber o link de recuperação.'}
              </p>
            </div>

            {!isResetMode ? (
              <form onSubmit={handleSolicitar} noValidate>
                <div className="form-group position-relative has-icon-left">
                  <label htmlFor="emailRecuperacao">E-mail</label>
                  <div className="position-relative">
                    <input
                      id="emailRecuperacao"
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                    <div className="form-control-icon">
                      <FaEnvelope />
                    </div>
                  </div>
                </div>

                <button className={`btn btn-primary password-recovery-btn ${loading ? 'is-loading' : ''}`} type="submit" disabled={loading}>
                  {loading && <span className="auth-btn-spinner" aria-hidden="true" />}
                  <span>{loading ? 'Enviando...' : 'Enviar link de recuperação'}</span>
                </button>
              </form>
            ) : (
              <form onSubmit={handleRedefinir} noValidate>
                <div className="form-group">
                  <label>E-mail</label>
                  <div className="password-recovery-email-readonly" title={email}>
                    {email}
                  </div>
                </div>

                <div className="form-group position-relative has-icon-left">
                  <label htmlFor="novaSenha">Nova senha</label>
                  <div className="position-relative">
                    <input
                      id="novaSenha"
                      type="password"
                      className="form-control"
                      value={senha}
                      onChange={e => setSenha(e.target.value)}
                      autoComplete="new-password"
                      required
                      minLength={6}
                    />
                    <div className="form-control-icon">
                      <FaLock />
                    </div>
                  </div>
                </div>

                <div className="form-group position-relative has-icon-left">
                  <label htmlFor="confirmarSenha">Confirmar senha</label>
                  <div className="position-relative">
                    <input
                      id="confirmarSenha"
                      type="password"
                      className="form-control"
                      value={senhaConfirmacao}
                      onChange={e => setSenhaConfirmacao(e.target.value)}
                      autoComplete="new-password"
                      required
                      minLength={6}
                    />
                    <div className="form-control-icon">
                      <FaLock />
                    </div>
                  </div>
                </div>

                <button className={`btn btn-primary password-recovery-btn ${loading ? 'is-loading' : ''}`} type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Salvando...
                    </>
                  ) : (
                    <span>Redefinir senha</span>
                  )}
                </button>
              </form>
            )}

            <div className="password-recovery-footer">
              <Link to="/login">Voltar para o login</Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
